import type { Sort } from '@/stores/sorting';
import type { InvoiceRequest, InvoiceResponse } from '@/types';
import type {
    CreditNotePayload,
    DocumentAttachment,
    InvoiceBulkActionResponse,
    InvoiceCheckoutResponse,
    PaymentStatusResponse,
    QuoteSignatureVerificationData,
    SignQuotePayload,
} from '@/types/invoice.ts';
import { sortToQuery } from '@/utils/misc.ts';
import type { AxiosInstance, AxiosResponse } from 'axios';

class InvoiceService {
    private readonly apiService;

    constructor(apiService: AxiosInstance) {
        this.apiService = apiService;
    }

    getPath(id?: string, isPublic = false) {
        return id ? `${!isPublic ? '/api' : ''}/invoices/${id}` : '/api/invoices';
    }

    index(page: number, filter?: object, sort: null | Sort = null, perPage?: number) {
        return this.apiService.get(this.getPath(), {
            params: { page: page, per_page: perPage, ...filter, ...sortToQuery(sort) },
        });
    }

    async show(id: string, pin?: string): Promise<AxiosResponse> {
        if (pin) {
            await this.apiService.auth.csrf();
            return this.apiService.post(`${this.getPath(id, true)}`, { pin });
        }
        return this.apiService.get(this.getPath(id));
    }

    store(request: InvoiceRequest) {
        return this.apiService.post(this.getPath(), request);
    }

    update(id: string, request: InvoiceRequest): Promise<AxiosResponse<InvoiceResponse>> {
        return this.apiService.put(this.getPath(id), request);
    }

    delete(id: string) {
        return this.apiService.delete(this.getPath(id));
    }

    credit(id: string, payload: CreditNotePayload): Promise<AxiosResponse<InvoiceResponse>> {
        return this.apiService.post(`${this.getPath(id)}/credit`, payload);
    }

    convert(id: string): Promise<AxiosResponse<InvoiceResponse>> {
        return this.apiService.post(`${this.getPath(id)}/convert`);
    }

    // ─── Attachments (MILO-159) ──────────────────────────────────────────────
    // `attachmentId` is the media uuid; the API never exposes the numeric id.

    attachments(id: string): Promise<AxiosResponse<{ data: DocumentAttachment[] }>> {
        return this.apiService.get(`${this.getPath(id)}/attachments`);
    }

    uploadAttachments(id: string, files: File[]): Promise<AxiosResponse<{ data: DocumentAttachment[] }>> {
        const formData = new FormData();
        files.forEach((file) => formData.append('files[]', file));

        return this.apiService.post(`${this.getPath(id)}/attachments`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    deleteAttachment(id: string, attachmentId: string) {
        return this.apiService.delete(`${this.getPath(id)}/attachments/${attachmentId}`);
    }

    downloadAttachment(id: string, attachmentId: string): Promise<AxiosResponse<Blob>> {
        return this.apiService.get(`${this.getPath(id)}/attachments/${attachmentId}/download`, {
            responseType: 'blob',
        });
    }

    /** Terms the document was sent under (MILO-352): token in the URL, PIN in the body. */
    publicTerms(id: string, pin: string): Promise<AxiosResponse<Blob>> {
        return this.apiService.post(`${this.getPath(id, true)}/terms`, { pin }, { responseType: 'blob' });
    }

    /** Right-of-withdrawal info a consumer was sent (MILO-360): token in the URL, PIN in the body. */
    publicWithdrawalInfo(id: string, pin: string): Promise<AxiosResponse<Blob>> {
        return this.apiService.post(`${this.getPath(id, true)}/withdrawal-info`, { pin }, { responseType: 'blob' });
    }

    /**
     * Register a consumer's withdrawal within the cooling-off period (MILO-361).
     * `confirmed` is the mandatory separate confirmation step (art. 6:230oa BW).
     */
    publicWithdraw(
        id: string,
        payload: { pin: string; name: string; email: string; statement?: string; confirmed: boolean },
    ): Promise<AxiosResponse> {
        return this.apiService.post(`${this.getPath(id, true)}/withdraw`, payload);
    }

    /** Customer-facing download: token in the URL, PIN in the body. */
    publicAttachment(id: string, pin: string, attachmentId: string): Promise<AxiosResponse<Blob>> {
        return this.apiService.post(
            `${this.getPath(id, true)}/attachments/${attachmentId}`,
            { pin },
            { responseType: 'blob' },
        );
    }

    actions(id: string): Promise<AxiosResponse> {
        return this.apiService.get(`${this.getPath(id)}/actions`);
    }

    publicActions(id: string, pin: string): Promise<AxiosResponse> {
        return this.apiService.post(`${this.getPath(id, true)}/actions`, {
            pin,
        });
    }

    setStatus(id: string, status: string, reason: string): Promise<AxiosResponse> {
        return this.apiService.post(`${this.getPath(id)}/status`, {
            status,
            reason,
        });
    }

    setPublicStatus(
        id: string,
        pin: string,
        status: string,
        reason: string,
        earlyStartRequested = false,
    ): Promise<AxiosResponse> {
        return this.apiService.post(`${this.getPath(id, true)}/status`, {
            pin,
            status,
            reason,
            // Consumer's express early-start request (MILO-360); the backend only
            // honours it on an approve for a consumer document.
            early_start_requested: earlyStartRequested,
        });
    }

    getInvoiceLineSuggestions(type: string, q: string): Promise<AxiosResponse> {
        return this.apiService.post(`${this.getPath()}/lines/autocomplete`, {
            type,
            q,
        });
    }

    getStatusLogs(id: string): Promise<AxiosResponse> {
        return this.apiService.get(`${this.getPath(id)}/status-logs`);
    }

    // The VAT codes on offer differ per company (territory, small business scheme), so always name it
    populators(companyId?: string): Promise<AxiosResponse> {
        return this.apiService.get(
            `${this.getPath()}/populators`,
            companyId ? { params: { company_id: companyId } } : undefined,
        );
    }

    sendEmail(id: string): Promise<AxiosResponse<void>> {
        return this.apiService.post(`${this.getPath(id)}/send`);
    }

    sendReminder(id: string): Promise<AxiosResponse<void>> {
        return this.apiService.post(`${this.getPath(id)}/reminder`);
    }

    downloadPdf(id: string): Promise<AxiosResponse<Blob>> {
        return this.apiService.get(`${this.getPath(id)}/pdf/download`, {
            responseType: 'blob',
        });
    }

    downloadUbl(id: string): Promise<AxiosResponse<Blob>> {
        return this.apiService.get(`${this.getPath(id)}/ubl/download`, {
            responseType: 'blob',
        });
    }

    publicUbl(id: string, pin: string): Promise<AxiosResponse<Blob>> {
        return this.apiService.post(`${this.getPath(id, true)}/ubl`, { pin }, { responseType: 'blob' });
    }

    publicPdf(id: string, pin: string): Promise<AxiosResponse<Blob>> {
        return this.apiService.post(
            `${this.getPath(id, true)}/pdf`,
            {
                pin,
            },
            {
                responseType: 'blob',
            },
        );
    }

    requestSignatureVerification(
        id: string,
        pin: string,
    ): Promise<AxiosResponse<{ data: QuoteSignatureVerificationData }>> {
        return this.apiService.post(`${this.getPath(id, true)}/sign/verification`, { pin });
    }

    // The early-start request (MILO-360) rides in SignQuotePayload; signed_by_email
    // is never sent — the backend derives the signer address from the OTP-verified
    // customer email (MILO-354).
    sign(id: string, payload: SignQuotePayload): Promise<AxiosResponse<Blob>> {
        return this.apiService.post(`${this.getPath(id, true)}/sign`, payload, { responseType: 'blob' });
    }

    // --- Online payments (MILO-322) ---

    /**
     * Start an online payment for a public invoice. Public endpoint, so it needs
     * a CSRF token first (like show()). Returns the Mollie checkout URL to
     * redirect the payer to. 422 = no longer payable, 403 = wrong pin.
     */
    async pay(id: string, pin: string): Promise<AxiosResponse<InvoiceCheckoutResponse>> {
        await this.apiService.auth.csrf();
        return this.apiService.post(`${this.getPath(id, true)}/pay`, { pin });
    }

    /** Read a public invoice's payment status by token only (no pin). 404 = none started / >48h old. */
    paymentStatus(id: string): Promise<AxiosResponse<PaymentStatusResponse>> {
        return this.apiService.get(`${this.getPath(id, true)}/payment-status`);
    }

    /**
     * Absolute URL for the "pay again" retry: a plain browser GET that starts a
     * fresh checkout and 302s to Mollie. Used with window.location (not XHR), so
     * it must be fully qualified against the API origin.
     */
    payRedirectUrl(id: string): string {
        return `${import.meta.env.VITE_API_BASE_URL}${this.getPath(id, true)}/pay`;
    }

    // Bulk actions
    bulkSendReminder(ids: string[]): Promise<AxiosResponse<{ data: InvoiceBulkActionResponse }>> {
        return this.apiService.post(`${this.getPath()}/bulk/reminder`, { ids });
    }

    bulkDownloadPdf(ids: string[]): Promise<AxiosResponse<Blob>> {
        return this.apiService.post(
            `${this.getPath()}/bulk/pdf/download`,
            { ids },
            {
                responseType: 'blob',
            },
        );
    }

    bulkSetStatus(ids: string[], status: string): Promise<{ data: AxiosResponse<InvoiceBulkActionResponse> }> {
        return this.apiService.post(`${this.getPath()}/bulk/status`, {
            ids,
            status,
        });
    }

    bulkDelete(ids: string[]): Promise<AxiosResponse<{ data: InvoiceBulkActionResponse }>> {
        return this.apiService.post(`${this.getPath()}/bulk/delete`, { ids });
    }
}

export default InvoiceService;
